import React from 'react';
import fhirpath from 'fhirpath';
import { View, StyleSheet } from '@react-pdf/renderer';

import { Solicitacao } from '@/models/Solicitacao';

import DocumentPanel from '@/features/pdf/components/DocumentPanel';
import ItemList from '@/features/pdf/components/ItemList';
import { LegalFooterContent } from '@/features/pdf/components/DocumentFooter';
import { SectionResumoType } from '@/features/enterprise/models/types';
import { medicationRequestTitle } from '@/features/fhir/helpers/medicationRequest';
import { codeableConceptDisplay } from '@/features/fhir/helpers/fhirpath';

import { renderableSections } from '../../../execucao/hooks/useSumarioSections';
import ClinicalDocumentLayout, {
  ClinicalDocumentBaseProps,
} from '../ClinicalDocumentLayout';
import SignaturePractitioner from '../SignaturePractitioner';

const styles = StyleSheet.create({
  sections: {
    rowGap: 8,
    flexDirection: 'column',
  },
});

const MedicationRequestItemList: React.FC<{
  items: fhir4.MedicationRequest[];
}> = ({ items }) => {
  const title = React.useCallback((item: fhir4.MedicationRequest) => {
    return medicationRequestTitle(item);
  }, []);

  const description = React.useCallback((item: fhir4.MedicationRequest) => {
    return fhirpath.evaluate(
      item,
      'MedicationRequest.dosageInstruction.text',
    )[0];
  }, []);

  return (
    <ItemList
      items={items.map(item => ({
        name: title(item),
        description: description(item),
      }))}
    />
  );
};
const ServiceRequestItemList: React.FC<{
  items: fhir4.ServiceRequest[];
}> = ({ items = [] }) => {
  const title = React.useCallback((item: fhir4.ServiceRequest) => {
    return codeableConceptDisplay(item, 'code');
  }, []);

  const description = React.useCallback((item: fhir4.ServiceRequest) => {
    return fhirpath.evaluate(item, 'ServiceRequest.note.text')[0];
  }, []);

  return (
    <ItemList
      items={items.map(item => ({
        name: title(item),
        description: description(item),
      }))}
    />
  );
};

interface Props extends ClinicalDocumentBaseProps {
  sumario: SectionResumoType[];
  solicitacoes: Solicitacao[];
}

export const DocumentPageResumoAtendimento: React.FC<Props> = ({
  encounter,
  sumario,
  solicitacoes,
  header,
  photo,
}) => {
  const sections = renderableSections(sumario, solicitacoes);

  const sectionContent = React.useCallback(
    (section: SectionResumoType) => {
      if (section.modelo === 'solicitacoes-medicamento') {
        const medications = solicitacoes
          .filter(item => item.tipo === 'prescricao-medica')
          .map(item => item.pedido as fhir4.MedicationRequest);

        return <MedicationRequestItemList items={medications} />;
      }

      if (section.modelo === 'solicitacoes-exame') {
        const exams = solicitacoes
          .filter(item => item.tipo === 'solicitacao-exame')
          .map(item => item.pedido as fhir4.ServiceRequest);

        return <ServiceRequestItemList items={exams} />;
      }

      return section.texto_renderizado;
    },
    [solicitacoes],
  );

  return (
    <ClinicalDocumentLayout
      header={header}
      photo={photo}
      encounter={encounter}
      title="Resumo de Atendimento"
      footer={
        <>
          <SignaturePractitioner encounter={encounter} />
          <LegalFooterContent />
        </>
      }
    >
      <View style={styles.sections}>
        {sections.map(section => (
          <DocumentPanel
            key={section.codigo}
            title={section.titulo}
            content={sectionContent(section)}
          />
        ))}
      </View>
    </ClinicalDocumentLayout>
  );
};

export default DocumentPageResumoAtendimento;
